import { useEffect, useState, useRef } from "react";
const NPSLogo = "/assets/NPSLogo.png";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import getRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService";
import PaymentService from "@/services/NPS/Stepper/APIs/PaymentService";
import { useNavigate, useLocation } from "react-router-dom";
import NavigationRoutes from "@/projects/NPS/Stepper/utils/navigationRoutes";

const PranSuccessScreen: React.FC<{
  pran: string;
  fullName: string;
  tierI: number;
  tierII: number;
  email: string;
  mobile: string;
  onBackToDashboard: () => void;
}> = ({ pran, fullName, tierI, tierII, email, mobile, onBackToDashboard }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [registrationDetails, setRegistrationDetails] =
    useState<Registration | null>(null);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [providerErrors, setProviderErrors] = useState<string[]>([]);
  const [isProviderPendingRegistration, setIsProviderPendingRegistration] =
    useState(false);
  const [pranFromState, setPranFromState] = useState<string | null>(null);
  const [isCheckingPran, setIsCheckingPran] = useState(false);
  const pranPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pranPollCountRef = useRef<number>(0);

  // Get PRAN from all possible sources
  const pranToDisplay =
    pranFromState ??
    pran ??
    registrationDetails?.initialDetails?.pranNumber ??
    registrationDetails?.provider_response?.pran ??
    "";

  const hasPran = !!pranToDisplay;

  useEffect(() => {
    // Get PRAN from route state if available
    const state = location.state as { pran?: string } | null;
    if (state?.pran) {
      setPranFromState(state.pran);
    }
  }, [location]);

  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      const response =
        await getRegistrationDetailsService.getRegistrationDetails();
      if (response.success) {
        setRegistrationDetails(response.registration);
        // Check if status is submitted and payment is pending
        if (
          response.registration?.status === "payment_pending" &&
          response.registration?.personalDtls?.payment_status === "pending"
        ) {
          setIsPaymentPending(true);
        }

        // Capture provider errors when registration is in progress but failed at provider
        const providerResponse = response.registration?.provider_response;
        if (
          response.registration?.status === "in_progress" &&
          providerResponse?.status === "Failure" &&
          Array.isArray(providerResponse?.fieldErrors) &&
          providerResponse.fieldErrors.length > 0
        ) {
          setProviderErrors(providerResponse.fieldErrors);
        }

        if (
          response.registration?.status === "completed" &&
          providerResponse?.status === "Pending for Registration"
        ) {
          setIsProviderPendingRegistration(true);
        }

        // Check if PRAN is available in provider_response
        if (providerResponse?.pran && !pranFromState) {
          setPranFromState(providerResponse.pran);
        }

        // Call track-status API if PRAN is not available yet and we have ackId
        const ackId = response.registration?.initialDetails?.ackId;
        if (
          !pranFromState &&
          !pran &&
          !response.registration?.initialDetails?.pranNumber &&
          ackId
        ) {
          setIsCheckingPran(true);
          // Wait a bit before calling track-status (in case PRAN is still being generated)
          setTimeout(async () => {
            try {
              const trackStatusResponse = await PaymentService.trackStatus({
                ackId: ackId,
              });
              if (
                trackStatusResponse?.success &&
                trackStatusResponse?.trackStatus?.pran &&
                trackStatusResponse.trackStatus.pran !== null
              ) {
                setPranFromState(trackStatusResponse.trackStatus.pran);
                setIsCheckingPran(false);
              } else {
                setIsCheckingPran(false);
              }
            } catch (error) {
              console.error("Error fetching track status:", error);
              setIsCheckingPran(false);
            }
          }, 2000); // Wait 2 seconds before calling
        }
      }
    };
    fetchRegistrationDetails();
  }, [pranFromState, pran]);

  // Poll for PRAN if not available yet
  useEffect(() => {
    // Clean up any existing polling
    if (pranPollIntervalRef.current) {
      clearInterval(pranPollIntervalRef.current);
      pranPollIntervalRef.current = null;
    }
    pranPollCountRef.current = 0;

    if (
      !hasPran &&
      registrationDetails?.initialDetails?.ackId &&
      !isCheckingPran
    ) {
      const ackId = registrationDetails.initialDetails.ackId;
      const maxPolls = 12; // Poll for 1 minute (12 * 5 seconds)

      const pollForPran = async () => {
        // Check current PRAN state
        const currentPran =
          pranFromState ??
          pran ??
          registrationDetails?.initialDetails?.pranNumber ??
          registrationDetails?.provider_response?.pran ??
          null;

        if (currentPran || pranPollCountRef.current >= maxPolls) {
          if (pranPollIntervalRef.current) {
            clearInterval(pranPollIntervalRef.current);
            pranPollIntervalRef.current = null;
          }
          return;
        }

        pranPollCountRef.current++;
        setIsCheckingPran(true);

        try {
          const trackStatusResponse = await PaymentService.trackStatus({
            ackId: ackId,
          });
          if (
            trackStatusResponse?.success &&
            trackStatusResponse?.trackStatus?.pran &&
            trackStatusResponse.trackStatus.pran !== null
          ) {
            setPranFromState(trackStatusResponse.trackStatus.pran);
            setIsCheckingPran(false);
            if (pranPollIntervalRef.current) {
              clearInterval(pranPollIntervalRef.current);
              pranPollIntervalRef.current = null;
            }
          } else {
            setIsCheckingPran(false);
          }
        } catch (error) {
          console.error("Error fetching track status:", error);
          setIsCheckingPran(false);
        }
      };

      // Start polling after 10 seconds, then every 5 seconds
      const initialTimeout = setTimeout(() => {
        pollForPran();
        pranPollIntervalRef.current = setInterval(pollForPran, 5000);
      }, 10000);

      return () => {
        clearTimeout(initialTimeout);
        if (pranPollIntervalRef.current) {
          clearInterval(pranPollIntervalRef.current);
          pranPollIntervalRef.current = null;
        }
      };
    }
  }, [
    hasPran,
    registrationDetails?.initialDetails?.ackId,
    isCheckingPran,
    pranFromState,
    pran,
    registrationDetails,
  ]);

  console.log("pran", pran);
  console.log("fullName", fullName);
  console.log("tierI", tierI);
  console.log("tierII", tierII);
  console.log("email", email);
  console.log("mobile", mobile);
  console.log("onBackToDashboard", onBackToDashboard);

  useEffect(() => {
    document.body.style.backgroundColor = "#FFF8F4"; // Light pink background as in screenshot
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  return (
    <div className="mn:p-6 flex min-h-screen items-center justify-center bg-[#f2f2f2] p-3">
      <div className="mx-auto w-full max-w-xl rounded-[16px] bg-white p-4 py-6 shadow-[0_2px_24px_0_rgba(35,105,142,0.08)] md:p-8 md:py-10">
        {/* ===== Icon - Checkmark or Clock based on payment status / failures ===== */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            {providerErrors.length > 0 ? (
              <div className="flex h-24 w-24 items-center justify-center rounded-xl border-4 border-[#D92D20] bg-[#FFF8F4]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-[#D92D20]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            ) : isPaymentPending ? (
              <div className="flex h-24 w-24 items-center justify-center rounded-xl bg-[#FFF8F4]">
                <img
                  src="/assets/clock.png"
                  alt="Payment Processing"
                  className="h-20 w-20"
                />
              </div>
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-xl border-4 border-[#228B22] bg-[#FFF8F4]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-[#228B22]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
            {!isPaymentPending && providerErrors.length === 0 && (
              <div className="absolute -right-2 -top-2 h-4 w-4 animate-pulse rounded-full bg-[#228B22]" />
            )}
          </div>
        </div>

        {/* ===== Success Message ===== */}
        <h2
          className={`mx-auto mb-4 max-w-[800px] text-center font-nunito text-[20px] font-bold md:text-[25px] ${
            providerErrors.length > 0 ? "text-[#D92D20]" : "text-[#000]"
          }`}
        >
          {providerErrors.length > 0
            ? "We couldn't complete your PRAN generation. Please contact your administrator for assistance."
            : isProviderPendingRegistration
              ? "Your registration has been submitted. We will notify you once PRAN generation is completed."
              : isPaymentPending
                ? "Your initial investment to NPS is submitted."
                : hasPran
                  ? "Congratulations! Your PRAN has been generated successfully."
                  : "Your PRAN is currently being generated. We will notify you once the process is completed.."}
        </h2>

        {/* ===== PRAN Number - Show if available ===== */}
        {!isPaymentPending && providerErrors.length === 0 && hasPran && (
          <div className="mb-6 rounded-lg bg-[#F5F5F5] p-4 text-center">
            <p className="mb-2 font-nunito text-sm font-semibold text-[#707070]">
              Permanent Retirement Account Number (PRAN)
            </p>
            <p className="font-nunito text-xl font-bold tracking-wider text-[#228B22] md:text-2xl">
              {pranToDisplay}
            </p>
          </div>
        )}

        {/* ===== Please Wait Message - Show if payment is pending ===== */}
        {isPaymentPending && (
          <div className="mb-6 text-center">
            <p className="text-md font-nunito font-semibold text-[#FFA500]">
              Please wait your payment is being processed.
            </p>
          </div>
        )}

        {/* ===== Info Text - Hide if payment is pending ===== */}
        {!isPaymentPending && providerErrors.length === 0 && (
          <>
            {isCheckingPran && !hasPran && (
              <p className="mb-4 text-center font-nunito text-sm font-semibold text-[#1DAB91]">
                Checking for your PRAN...
              </p>
            )}
            <p className="mb-6 text-center font-nunito text-sm font-semibold text-[#707070]">
              {hasPran
                ? "The PRAN has been sent to your verified email ID and Mobile Number."
                : "The PRAN will be sent to your verified email ID and Mobile Number once generated."}
            </p>
          </>
        )}

        {/* ===== User Details ===== */}
        {providerErrors.length === 0 && (
          <div className="mx-12 mb-6 flex justify-between gap-4 text-center">
            <div className="col-span-2">
              <p className="font-nunito text-sm font-semibold text-[#707070]">
                Full Name
              </p>
              <p className="font-nunito text-sm font-semibold text-[#2F363F]">
                {fullName ??
                  registrationDetails?.personalDtls?.subFstName ??
                  ""}
              </p>
            </div>
            <div className="float-right">
              <p className="font-nunito text-sm font-semibold text-[#707070]">
                Tier I
              </p>
              <p className="font-nunito text-sm font-semibold text-[#2F363F]">
                ₹
                {tierI ??
                  registrationDetails?.personalDtls?.pran_total_contribution ??
                  ""}
              </p>
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center justify-center">
          <div className="flex cursor-pointer items-center gap-3">
            <img
              src={NPSLogo}
              alt="NPS Trust"
              onClick={() => navigate(NavigationRoutes.HOME)}
              className="h-10 w-auto md:h-12"
            />
            <span className="md:text-md font-nunito text-sm font-semibold text-[#707070]">
              Powered by NPS TRUST
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PranSuccessScreen;
